import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import toast from 'react-hot-toast';
import {
  FileText,
  RefreshCw,
  Filter,
  Search,
  Calendar,
  User,
  Building2,
  CreditCard,
  Settings,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Download
} from 'lucide-react';

interface LogEntry {
  id: string;
  type: 'purchase' | 'user_action' | 'system' | 'error' | 'business';
  action: string;
  details: string;
  userId?: string;
  userEmail?: string;
  businessId?: string;
  businessName?: string;
  timestamp: any;
  severity: 'info' | 'warning' | 'error' | 'success';
}

const SuperAdminLogsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    const allLogs: LogEntry[] = [];

    try {
      // 1. Cargar compras recientes como logs
      try {
        const purchasesQuery = query(
          collection(db, 'purchases'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const purchasesSnap = await getDocs(purchasesQuery);
        purchasesSnap.docs.forEach(doc => {
          const data = doc.data();
          allLogs.push({
            id: `purchase_${doc.id}`,
            type: 'purchase',
            action: 'Compra realizada',
            details: `${data.productName || data.productType} - €${((data.amount || 0) / 100).toFixed(2)}`,
            userId: data.userId,
            businessId: data.businessId,
            timestamp: data.createdAt,
            severity: data.status === 'completed' ? 'success' : 'warning'
          });
        });
      } catch (e) {
        console.log('No se pudieron cargar compras');
      }

      // 2. Cargar broadcasts como logs
      try {
        const broadcastsQuery = query(
          collection(db, 'broadcasts'),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        const broadcastsSnap = await getDocs(broadcastsQuery);
        broadcastsSnap.docs.forEach(doc => {
          const data = doc.data();
          allLogs.push({
            id: `broadcast_${doc.id}`,
            type: 'system',
            action: 'Mensaje enviado',
            details: `"${data.title}" a ${data.targetAudience}`,
            timestamp: data.createdAt,
            severity: 'info'
          });
        });
      } catch (e) {
        console.log('No se pudieron cargar broadcasts');
      }

      // 3. Cargar negocios recientes como logs
      try {
        const businessesQuery = query(
          collection(db, 'businesses'),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        const businessesSnap = await getDocs(businessesQuery);
        businessesSnap.docs.forEach(doc => {
          const data = doc.data();
          allLogs.push({
            id: `business_${doc.id}`,
            type: 'business',
            action: 'Negocio registrado',
            details: data.name,
            businessId: doc.id,
            businessName: data.name,
            userEmail: data.adminEmail,
            timestamp: data.createdAt,
            severity: 'success'
          });
        });
      } catch (e) {
        console.log('No se pudieron cargar negocios');
      }

      // 4. Cargar usuarios recientes
      try {
        const usersQuery = query(
          collection(db, 'users'),
          orderBy('createdAt', 'desc'),
          limit(30)
        );
        const usersSnap = await getDocs(usersQuery);
        usersSnap.docs.forEach(doc => {
          const data = doc.data();
          allLogs.push({
            id: `user_${doc.id}`,
            type: 'user_action',
            action: 'Usuario registrado',
            details: `${data.firstName} ${data.lastName} (${data.email})`,
            userId: doc.id,
            userEmail: data.email,
            businessId: data.businessId,
            timestamp: data.createdAt,
            severity: data.status === 'active' ? 'success' : 'warning'
          });
        });
      } catch (e) {
        console.log('No se pudieron cargar usuarios');
      }

      // Ordenar por timestamp
      allLogs.sort((a, b) => {
        const dateA = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
        const dateB = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
        return dateB.getTime() - dateA.getTime();
      });

      setLogs(allLogs);
      setFilteredLogs(allLogs);
    } catch (e) {
      console.error('Error cargando logs:', e);
      toast.error('Error cargando logs');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar logs
  useEffect(() => {
    let filtered = [...logs];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log =>
        log.action.toLowerCase().includes(term) ||
        log.details.toLowerCase().includes(term) ||
        log.userEmail?.toLowerCase().includes(term) ||
        log.businessName?.toLowerCase().includes(term)
      );
    }
    
    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.type === filterType);
    }
    
    if (filterSeverity !== 'all') {
      filtered = filtered.filter(log => log.severity === filterSeverity);
    }
    
    setFilteredLogs(filtered);
  }, [searchTerm, filterType, filterSeverity, logs]);

  const handleExport = () => {
    const csvContent = [
      ['Fecha', 'Tipo', 'Acción', 'Detalles', 'Usuario', 'Severidad'].join(','),
      ...filteredLogs.map(log => [
        formatDate(log.timestamp),
        log.type,
        log.action,
        `"${log.details}"`,
        log.userEmail || '',
        log.severity
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `logs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Logs exportados');
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'purchase':
        return <CreditCard className="w-4 h-4 text-green-500" />;
      case 'user_action':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'business':
        return <Building2 className="w-4 h-4 text-purple-500" />;
      case 'system':
        return <Settings className="w-4 h-4 text-gray-500" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'success':
        return <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full">OK</span>;
      case 'warning':
        return <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 rounded-full">Aviso</span>;
      case 'error':
        return <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full">Error</span>;
      default:
        return <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 rounded-full">Info</span>;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'purchase': return 'Compra';
      case 'user_action': return 'Usuario';
      case 'business': return 'Negocio';
      case 'system': return 'Sistema';
      case 'error': return 'Error';
      default: return type;
    }
  };

  // Estadísticas rápidas
  const stats = {
    total: logs.length,
    purchases: logs.filter(l => l.type === 'purchase').length,
    users: logs.filter(l => l.type === 'user_action').length,
    errors: logs.filter(l => l.severity === 'error').length,
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-7 h-7 text-amber-500" />
            Logs y Auditoría
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Registro de actividad del sistema
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
          <button
            onClick={loadLogs}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
          <p className="text-xs text-gray-500">Total Registros</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.purchases}</p>
          <p className="text-xs text-gray-500">Compras</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.users}</p>
          <p className="text-xs text-gray-500">Usuarios</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-2xl font-bold text-red-600">{stats.errors}</p>
          <p className="text-xs text-gray-500">Errores</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar en logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
          >
            <option value="all">Todos los tipos</option>
            <option value="purchase">Compras</option>
            <option value="user_action">Usuarios</option>
            <option value="business">Negocios</option>
            <option value="system">Sistema</option>
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
          >
            <option value="all">Todas las severidades</option>
            <option value="success">Exitoso</option>
            <option value="info">Info</option>
            <option value="warning">Aviso</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>

      {/* Lista de Logs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalles</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLogs.slice(0, 100).map(log => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      <span className="text-xs">{formatDate(log.timestamp)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(log.type)}
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getTypeLabel(log.type)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    {log.action}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 max-w-xs truncate">
                    {log.details}
                    {log.userEmail && (
                      <span className="block text-xs text-gray-400">{log.userEmail}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {getSeverityBadge(log.severity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredLogs.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p>No se encontraron registros</p>
          </div>
        )}

        {filteredLogs.length > 100 && (
          <div className="p-3 text-center text-sm text-gray-500 bg-gray-50 dark:bg-gray-700/50">
            Mostrando 100 de {filteredLogs.length} registros
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminLogsPage;











